import {
  SESSION_EXPIRY_HOLD_DURATION_MS,
  SESSION_EXPIRY_HOLD_MAX_MS,
  SESSION_EXPIRY_HOLD_RENEWAL_MS,
  SESSION_EXPIRY_HOLD_STORAGE_KEY,
} from 'config'
import { readSharedAuthTimeEntry } from './sharedAuthTime'
import { tryStorageAccess } from './tryStorageAccess'

// Signing out in one tab signs out all the tabs of the browser, so a tab can keep
// the other ones from ending the session. The holds are kept in localStorage,
// which every tab reads at once.
type HeldUntilByHoldId = Record<string, number>

// A tab decides on its own whether it can end the session
const ownHoldIds = new Set<string>()

const getActiveHolds = (): HeldUntilByHoldId => {
  try {
    const holds: HeldUntilByHoldId = JSON.parse(
      window.localStorage.getItem(SESSION_EXPIRY_HOLD_STORAGE_KEY) ?? '{}'
    )
    return Object.fromEntries(
      Object.entries(holds).filter(([, heldUntil]) => heldUntil > Date.now())
    )
  } catch {
    return {}
  }
}

const saveHolds = (holds: HeldUntilByHoldId) =>
  tryStorageAccess(() =>
    window.localStorage.setItem(
      SESSION_EXPIRY_HOLD_STORAGE_KEY,
      JSON.stringify(holds)
    )
  )

const releaseHold = (holdId: string) => {
  ownHoldIds.delete(holdId)
  const holds = getActiveHolds()
  delete holds[holdId]
  saveHolds(holds)
}

// Keeps the other tabs from ending the session until the returned function is
// called, or the session is extended or ended in any tab. The other tabs wait
// for it only for a limited time. The hold is renewed meanwhile, so the hold of
// a closed tab ends soon.
export const holdSessionExpiry = () => {
  const holdId = `${Date.now()}-${Math.random()}`
  const sharedAuthTimeAtStart = readSharedAuthTimeEntry()

  const stopHold = () => {
    clearInterval(renewalIntervalId)
    releaseHold(holdId)
  }

  const renewHold = () => {
    if (readSharedAuthTimeEntry() !== sharedAuthTimeAtStart) {
      stopHold()
      return
    }

    saveHolds({
      ...getActiveHolds(),
      [holdId]: Date.now() + SESSION_EXPIRY_HOLD_DURATION_MS,
    })
  }

  ownHoldIds.add(holdId)
  const renewalIntervalId = setInterval(
    renewHold,
    SESSION_EXPIRY_HOLD_RENEWAL_MS
  )
  renewHold()

  return stopHold
}

// After it the session is ended, even if a tab still holds its expiry
export const getSessionExpiryHeldAtMostUntil = (sessionExpiresAt?: number) =>
  sessionExpiresAt && sessionExpiresAt + SESSION_EXPIRY_HOLD_MAX_MS

// 0 when no other tab holds the session expiry
export const getSessionExpiryHeldByOtherTabsUntil = () =>
  Math.max(
    0,
    ...Object.entries(getActiveHolds())
      .filter(([holdId]) => !ownHoldIds.has(holdId))
      .map(([, heldUntil]) => heldUntil)
  )
